import { supabase } from '@/integrations/supabase/client';
import { getDeviceId } from '@/utils/device';
import OfflineLogger from '@/utils/offlineLogger';

export type SyncStatusValue = 'success' | 'warning' | 'error' | 'pending';

export interface SyncErrorDetail {
  message: string;
  code?: string;
  occurredAt?: string;
  metadata?: Record<string, unknown>;
}

interface UpsertSyncStatusParams {
  userId: string;
  entity: string;
  status: SyncStatusValue;
  lastSyncedAt?: string | Date;
  errorDetails?: SyncErrorDetail[];
}

interface AppendSyncErrorParams {
  userId: string;
  entity: string;
  error: SyncErrorDetail;
  status?: SyncStatusValue;
}

interface UpsertSyncMetricsParams {
  userId: string;
  totalSyncs: number;
  successfulSyncs: number;
  failedSyncs: number;
  averageDurationMs: number;
  metadata?: Record<string, unknown>;
}

export interface RemoteSyncStatus {
  userId: string;
  deviceId: string;
  entity: string;
  status: SyncStatusValue | string;
  lastSyncedAt: string | null;
  errorDetails: SyncErrorDetail[];
  updatedAt: string;
}

export interface RemoteSyncMetrics {
  userId: string;
  deviceId: string;
  collectedAt: string;
  totalSyncs: number;
  successfulSyncs: number;
  failedSyncs: number;
  averageDurationMs: number;
  metadata: Record<string, unknown>;
  updatedAt: string;
}

class SyncStatusService {
  private getDeviceId(): string {
    try {
      return getDeviceId();
    } catch (error) {
      OfflineLogger.error('SyncStatusService.getDeviceId', error);
      return 'unknown-device';
    }
  }

  private normalizeTimestamp(value?: string | Date): string | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    try {
      return new Date(value).toISOString();
    } catch (error) {
      OfflineLogger.error('SyncStatusService.normalizeTimestamp', error);
      return null;
    }
  }

  async upsertSyncStatus({ userId, entity, status, lastSyncedAt, errorDetails }: UpsertSyncStatusParams) {
    const deviceId = this.getDeviceId();

    try {
      const payload = {
        user_id: userId,
        device_id: deviceId,
        entity,
        status,
        last_synced_at: this.normalizeTimestamp(lastSyncedAt) ?? new Date().toISOString(),
        error_details: (errorDetails && errorDetails.length > 0 ? errorDetails : []) as unknown,
      };

      const { error } = await supabase
        .from('user_sync_statuses')
        .upsert(payload, { onConflict: 'user_id,device_id,entity' });

      if (error) {
        throw error;
      }

      OfflineLogger.info('SyncStatusService.upsertSyncStatus', `Stored status for ${entity}: ${status}`);
    } catch (error) {
      OfflineLogger.error('SyncStatusService.upsertSyncStatus', error);
    }
  }

  async appendSyncError({ userId, entity, error, status }: AppendSyncErrorParams) {
    const deviceId = this.getDeviceId();

    try {
      const { data, error: selectError } = await supabase
        .from('user_sync_statuses')
        .select('error_details')
        .eq('user_id', userId)
        .eq('device_id', deviceId)
        .eq('entity', entity)
        .maybeSingle();

      if (selectError) {
        throw selectError;
      }

      const currentErrors = Array.isArray(data?.error_details)
        ? (data?.error_details as SyncErrorDetail[])
        : [];

      const newError: SyncErrorDetail = {
        ...error,
        occurredAt: error.occurredAt ?? new Date().toISOString(),
      };

      const { error: upsertError } = await supabase
        .from('user_sync_statuses')
        .upsert(
          {
            user_id: userId,
            device_id: deviceId,
            entity,
            status: status ?? 'error',
            last_synced_at: new Date().toISOString(),
            error_details: [...currentErrors, newError] as unknown,
          },
          { onConflict: 'user_id,device_id,entity' }
        );

      if (upsertError) {
        throw upsertError;
      }

      OfflineLogger.warn('SyncStatusService.appendSyncError', `Registered sync error for ${entity}`);
    } catch (serviceError) {
      OfflineLogger.error('SyncStatusService.appendSyncError', serviceError);
    }
  }

  async upsertSyncMetrics({
    userId,
    totalSyncs,
    successfulSyncs,
    failedSyncs,
    averageDurationMs,
    metadata,
  }: UpsertSyncMetricsParams) {
    const deviceId = this.getDeviceId();

    try {
      const payload = {
        user_id: userId,
        device_id: deviceId,
        collected_at: new Date().toISOString(),
        total_syncs: totalSyncs,
        successful_syncs: successfulSyncs,
        failed_syncs: failedSyncs,
        average_duration_ms: averageDurationMs,
        metadata: metadata ?? {},
      };

      const { error } = await supabase
        .from('user_sync_metrics')
        .upsert(payload, { onConflict: 'user_id,device_id' });

      if (error) {
        throw error;
      }

      OfflineLogger.info('SyncStatusService.upsertSyncMetrics', 'Aggregated metrics stored');
    } catch (error) {
      OfflineLogger.error('SyncStatusService.upsertSyncMetrics', error);
    }
  }

  async fetchStatuses(userId: string): Promise<RemoteSyncStatus[]> {
    const deviceId = this.getDeviceId();

    try {
      const { data, error } = await supabase
        .from('user_sync_statuses')
        .select('user_id, device_id, entity, status, last_synced_at, error_details, updated_at')
        .eq('user_id', userId)
        .eq('device_id', deviceId)
        .order('entity', { ascending: true });

      if (error) {
        throw error;
      }

      return (data ?? []).map((row) => ({
        userId: row.user_id,
        deviceId: row.device_id,
        entity: row.entity,
        status: row.status,
        lastSyncedAt: row.last_synced_at,
        errorDetails: Array.isArray(row.error_details) ? (row.error_details as SyncErrorDetail[]) : [],
        updatedAt: row.updated_at,
      }));
    } catch (error) {
      OfflineLogger.error('SyncStatusService.fetchStatuses', error);
      return [];
    }
  }

  async fetchMetrics(userId: string): Promise<RemoteSyncMetrics | null> {
    const deviceId = this.getDeviceId();

    try {
      const { data, error } = await supabase
        .from('user_sync_metrics')
        .select('user_id, device_id, collected_at, total_syncs, successful_syncs, failed_syncs, average_duration_ms, metadata, updated_at')
        .eq('user_id', userId)
        .eq('device_id', deviceId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        return null;
      }

      return {
        userId: data.user_id,
        deviceId: data.device_id,
        collectedAt: data.collected_at,
        totalSyncs: data.total_syncs,
        successfulSyncs: data.successful_syncs,
        failedSyncs: data.failed_syncs,
        averageDurationMs: data.average_duration_ms,
        metadata: (data.metadata as Record<string, unknown>) ?? {},
        updatedAt: data.updated_at,
      };
    } catch (error) {
      OfflineLogger.error('SyncStatusService.fetchMetrics', error);
      return null;
    }
  }
}

export const syncStatusService = new SyncStatusService();
